<script generic="Model" lang="ts" setup>
  import { computed, reactive, ref } from 'vue';
  import type { Component as VueComponent } from 'vue';
  import { groupBy, isFunction } from 'lodash-es';
  import useGlobalDialog from '@/common/composables/useGlobalDialog';
  import { useI18n } from '@/common/composables/vue-i18n.composable';

  type Action = {
    label: string;
    type?: string;
    click: (model?: Model) => void;
    slotName: 'footer' | 'footer-aside';
    disabled?: boolean;
    icon?: string;
    dataTestId: string;
    component?: VueComponent;
    attrOrProp?: Record<string, any>;
  };

  const { isRevealed, onReveal, cancel, confirm } = useGlobalDialog();
  const { t } = useI18n();

  const dialog = ref<Partial<Dialog>>();
  const model = ref<Model>();
  const actions = ref<Action[] | undefined>();
  const testId = ref('');
  const confirmButtonDisabled = ref<(model?: Model) => boolean>(() => false);
  const text = ref<string | undefined>();

  const component = reactive<{
    name: string | unknown;
    attrOrProp: Record<string, any> | undefined;
  }>({
    name: null,
    attrOrProp: undefined
  });

  onReveal((params: GlobalDialogParams<Model>) => {
    dialog.value = params.dialog;
    actions.value = params.actions;
    component.name = params.component?.name;
    component.attrOrProp = params.component?.attrOrProp;
    text.value = params.text;
    testId.value = params.testId || 'global-dialog';
    model.value = params.model;
    if (isFunction(params.confirmButtonDisabled)) {
      confirmButtonDisabled.value = params.confirmButtonDisabled;
    }
  });

  const defaultActions = computed<Action[]>(() => [
    {
      label: t('Cancel'),
      slotName: 'footer',
      type: 'secondary',
      click: () => cancel(),
      dataTestId: 'cancel'
    },
    {
      label: t('Confirm'),
      slotName: 'footer',
      type: 'primary',
      click: () => confirm(model.value),
      disabled: confirmButtonDisabled.value(model.value),
      dataTestId: 'confirm'
    }
  ]);

  const groupByActions = computed(() => groupBy(actions.value || defaultActions.value, 'slotName'));

  const getActionComponent = (button: Action) => button.component || 'el-button';

  const getComponentAttributes = (button: Action) => {
    const attrs: Record<string, any> = {};
    return {
      ...attrs,
      ...button.attrOrProp
    };
  };

  const disableActionButton = (actionDisabled: Action['disabled']) => isFunction(actionDisabled) ? actionDisabled(model.value) : actionDisabled;

  const updateModel = (value: Model) => {
    model.value = value;
  };
</script>

<template>
  <el-dialog
    :visible="isRevealed"
    v-bind="dialog"
    :data-testid="testId"
    class="dialog v-global-dialog"
    @close="cancel"
  >
    <component
      :is="component.name"
      v-if="component.name"
      v-bind="{
        model,
        ...component.attrOrProp
      }"
      @update:model="updateModel"
    />
    <div
      v-else
      class="ma-24"
    >
      {{ text }}
    </div>
    <template
      v-for="(buttons, slotName) in groupByActions"
      #[slotName]
    >
      <component
        :is="getActionComponent(button)"
        v-for="button in buttons"
        :key="button.label"
        :type="button.type"
        :disabled="disableActionButton(button.disabled)"
        :icon="button.icon"
        :data-testid="button.dataTestId"
        v-bind="getComponentAttributes(button)"
        @click="() => button.click(model)"
      >
        {{ button.label }}
      </component>
    </template>
  </el-dialog>
</template>

<style lang="scss" scoped>
.footer {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}
</style>
